import {appUrl} from '@frontend/AppUrl'
import {trans} from '@frontend/helpers'
import {useAuthStore} from '@frontend/stores'
import type {RegionConfig} from '@frontend/types/Locales'
import {
    type SyntheticEvent,
    useLayoutEffect,
    useRef,
} from 'react'
import {Link} from 'react-router-dom'
import {Button} from 'swayok-react-mdb-ui-kit/components/Button/Button'
import {MaskedNumericInput} from 'swayok-react-mdb-ui-kit/components/Input/MaskedNumericInput'
import {HtmlContent} from 'swayok-react-mdb-ui-kit/components/Typography/HtmlContent'
import {useEventCallback} from 'swayok-react-mdb-ui-kit/helpers/useEventCallback'
import {AnalyticsEventReporter} from '../../../../helpers/AnalyticsEventReporter'

interface LoginFormStep1Props {
    phone: string
    phoneConfig: RegionConfig['phone']
    phoneError: string
    disabled: boolean
    registration?: boolean
    setPhone: (phone: string) => void
    onSubmit: () => void
}

// Форма ввода номера телефона.
export function LoginFormStep1(props: LoginFormStep1Props) {
    const {
        phone,
        phoneConfig,
        phoneError,
        disabled,
        registration,
        setPhone,
        onSubmit,
    } = props

    const cancelUserAuth = useAuthStore(
        state => state.cancelUserAuth
    )

    const inputRef = useRef<HTMLInputElement>(null)

    const handleSubmit = useEventCallback((
        e: SyntheticEvent
    ): void => {
        e.preventDefault()
        AnalyticsEventReporter.loginFormSendSmsCodeButtonClicked(
            disabled,
            !!registration,
            phone.trim().length > 0,
            !phoneError
        )
        if (!disabled) {
            onSubmit()
        }
    })

    // Сфокусировать поле ввода кода при отображении формы.
    useLayoutEffect(() => {
        if (inputRef.current) {
            inputRef.current.setSelectionRange(
                phoneConfig.minCursorPosition,
                phoneConfig.minCursorPosition
            )
            inputRef.current.focus()
        }
    }, [phoneConfig.minCursorPosition])

    return (
        <div>
            <div className="px-4 py-2">
                <div className="fs-5 fw-500">
                    {
                        registration
                            ? trans().auth.registration.header_phone_verification
                            : trans().auth.login.header
                    }
                </div>
                <form
                    className="mt-4"
                    onSubmit={handleSubmit}
                >
                    <MaskedNumericInput
                        inputRef={inputRef}
                        autoComplete="tel-local"
                        label={trans().auth.login.phone_number}
                        value={phone}
                        active
                        placeholder={phoneConfig.template}
                        template={phoneConfig.template}
                        minCursorPosition={phoneConfig.minCursorPosition}
                        onChange={(_e, cleanValue: string) => {
                            setPhone(cleanValue)
                        }}
                        disabled={disabled}
                        invalid={phoneError.length > 0}
                        validationMessage={phoneError}
                        trackBehaviorAs="loginPhoneInput"
                    />
                    <Button
                        type="submit"
                        color="blue"
                        className="full-width"
                        disabled={disabled}
                    >
                        {
                            registration
                                ? trans().auth.registration.do_login
                                : trans().auth.login.do_login
                        }
                    </Button>
                </form>
            </div>
            {!registration && (
                <div className="mt-4 pt-4 pb-1 px-3 border-top">
                    <div>
                        <Link
                            to={appUrl().registration}
                            onClick={() => cancelUserAuth(true)}
                            className="link-blue"
                        >
                            {trans().auth.login.go_to_registration}
                        </Link>
                    </div>
                    <HtmlContent
                        block
                        className="mt-3 fs-7 text-muted"
                        html={trans().auth.login.agreements_notice}
                    />
                </div>
            )}
        </div>
    )
}
